import type { ClubhouseAdKitDto } from "entities/responses/adkit";
import type { ClubhouseAdKitImageDto } from "entities/responses/adkitImage";
import {
  formatAspectRatioLabel,
  type PlacementCatalogEntry,
} from "../../placementCatalog";
import { pluralize } from "../../pluralize";

export type ImageLibraryTab = "ALL" | "IN_USE" | "NOT_USED" | "DELETED";

export const IMAGE_LIBRARY_TABS: ImageLibraryTab[] = [
  "ALL",
  "IN_USE",
  "NOT_USED",
  "DELETED",
];

export const IMAGE_LIBRARY_TAB_LABEL: Record<ImageLibraryTab, string> = {
  ALL: "All",
  IN_USE: "In use",
  NOT_USED: "Not used",
  DELETED: "Recently deleted",
};

export const IMAGE_LIBRARY_TAB_SLUG: Record<ImageLibraryTab, string> = {
  ALL: "all",
  IN_USE: "in-use",
  NOT_USED: "not-used",
  DELETED: "deleted",
};

export const IMAGE_LIBRARY_TAB_PARAM: Record<string, ImageLibraryTab> =
  Object.fromEntries(
    Object.entries(IMAGE_LIBRARY_TAB_SLUG).map(([tab, slug]) => [
      slug,
      tab as ImageLibraryTab,
    ]),
  );

export const IMAGE_LIBRARY_TAB_EMPTY: Record<ImageLibraryTab, string> = {
  ALL: "No images yet.",
  IN_USE: "No image is in a campaign yet.",
  NOT_USED: "Every image is in at least one campaign.",
  DELETED: "Nothing deleted recently.",
};

/** Within ~5% of a placement's shape counts as a fit (design note eng6). */
const FIT_TOLERANCE = 0.05;

export function imageDisplayName(image: ClubhouseAdKitImageDto): string {
  return image.name?.trim() || image.fileName?.trim() || "Untitled image";
}

/**
 * Campaign names per image id. The API has no usage field, so it comes from
 * each campaign's instances. Archived campaigns aren't sent to simulators,
 * so they don't count as using an image.
 */
export function buildImageUsage(
  campaigns: Pick<ClubhouseAdKitDto, "name" | "deleted" | "adLocations">[],
): Map<string, string[]> {
  const usage = new Map<string, string[]>();
  for (const campaign of campaigns) {
    if (campaign.deleted) continue;
    const imageIds = new Set<string>();
    for (const location of campaign.adLocations ?? []) {
      if (location.deleted) continue;
      for (const instance of location.adInstances ?? []) {
        if (!instance.deleted) imageIds.add(String(instance.imageId));
      }
    }
    for (const id of imageIds) {
      usage.set(id, [...(usage.get(id) ?? []), campaign.name]);
    }
  }
  return usage;
}

export type ImageBuckets = Record<ImageLibraryTab, ClubhouseAdKitImageDto[]>;

export function bucketImages(
  images: ClubhouseAdKitImageDto[],
  usage: Map<string, string[]>,
): ImageBuckets {
  const live = images.filter((image) => !image.deleted);
  const isUsed = (image: ClubhouseAdKitImageDto) =>
    (usage.get(String(image.id))?.length ?? 0) > 0;
  return {
    ALL: live,
    IN_USE: live.filter(isUsed),
    NOT_USED: live.filter((image) => !isUsed(image)),
    DELETED: images.filter((image) => image.deleted),
  };
}

export function filterImagesBySearch(
  images: ClubhouseAdKitImageDto[],
  search: string,
): ClubhouseAdKitImageDto[] {
  const term = search.trim().toLowerCase();
  if (!term) return images;
  return images.filter((image) =>
    imageDisplayName(image).toLowerCase().includes(term),
  );
}

function imageAspectRatio(image: ClubhouseAdKitImageDto): number | undefined {
  const d = image.dimensions;
  if (!d || !d.width || !d.height) return undefined;
  return d.aspectRatio ?? d.width / d.height;
}

/** "1000 × 500", or "" when the API didn't send dimensions. */
export function formatImageDimensions(image: ClubhouseAdKitImageDto): string {
  const d = image.dimensions;
  if (!d || !d.width || !d.height) return "";
  return `${Math.round(d.width)} × ${Math.round(d.height)}`;
}

/** "1000 × 500 · 2:1", or "" when the API didn't send dimensions. */
export function formatImageSize(image: ClubhouseAdKitImageDto): string {
  const d = image.dimensions;
  const ratio = imageAspectRatio(image);
  if (!d || ratio === undefined) return "";
  return `${Math.round(d.width)} × ${Math.round(d.height)} · ${formatAspectRatioLabel(ratio)}`;
}

/** Whether a width÷height ratio is within ~5% of a placement's shape. */
export function ratioFitsPlacement(
  ratio: number,
  placement: Pick<PlacementCatalogEntry, "aspectRatio">,
): boolean {
  if (!placement.aspectRatio) return false;
  return (
    Math.abs(ratio - placement.aspectRatio) / placement.aspectRatio <=
    FIT_TOLERANCE
  );
}

export function imageFitsPlacement(
  image: ClubhouseAdKitImageDto,
  placement: Pick<PlacementCatalogEntry, "aspectRatio">,
): boolean {
  const ratio = imageAspectRatio(image);
  return ratio !== undefined && ratioFitsPlacement(ratio, placement);
}

export function placementsImageFits(
  image: ClubhouseAdKitImageDto,
  placements: PlacementCatalogEntry[],
): PlacementCatalogEntry[] {
  return placements.filter((p) => imageFitsPlacement(image, p));
}

export function formatImageUsage(campaignNames: string[] | undefined): string {
  return campaignNames?.length
    ? `In ${pluralize(campaignNames.length, "campaign")}`
    : "Not used yet";
}
